import "./styles/AppliancePage.scss";

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { graphql } from "gatsby";
import { useTranslation } from "react-i18next";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";

import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { ServicesContext, useDeviceDetect } from "@sbi-international/boutik.gatsby.plugin.services";

import { useAllHeaters } from "../hooks/useAllHeaters";
import { ProductPageTemplateInfo } from "../models/productPageTemplateInfo";
import { Layout } from "../components/layout";
import SEO from "../components/Seo";
import Breadcrumb from "../components/primitives/Breadcrumb";
import {
    ApplianceDetails,
    ApplianceImage,
    ApplianceInfo,
    ApplianceReviews,
} from "../components/appliance";
import { ApplianceSingle } from "../components/listing";
import buttonArrow from "../images/icons/button-arrow.svg";
import { useAllProductReviews } from "../hooks/useAllProductReviews";

export const query = graphql`
    query AppliancePage($code: String!) {
        appliance: productsJson(code: { eq: $code }) {
            ...appliancePageTemplateFragment
        }
        suggestionsCMS: allProductsJson(filter: { code: { eq: "drolet_appliance-suggestions" } }) {
            nodes {
                fields {
                    banniere_desktop {
                        childImageSharp {
                            gatsbyImageData(layout: CONSTRAINED)
                        }
                    }
                    banniere_mobile {
                        childImageSharp {
                            gatsbyImageData(layout: CONSTRAINED)
                        }
                    }
                }
                attributes {
                    texte_1 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_2 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function AppliancePage({
    data,
    location,
}: {
    data: Queries.AppliancePageQuery;
    location: any;
}) {
    const [productPageTemplateInfo, setProductPageTemplateInfo] = useState<ProductPageTemplateInfo>(
        { static: data.appliance ?? ({} as Queries.appliancePageTemplateFragmentFragment) }
    );
    const allProductReviews = useAllProductReviews();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const appliance = data.appliance;
    const reviews =
        allProductReviews.find(
            (review: Queries.allProductReviewsNodeFragmentFragment) =>
                review.sku === appliance?.code
        ) ??
        ({
            sku: appliance?.code as string,
            average: 0,
            count: 0,
            reviews: [],
        } as Queries.allProductReviewsNodeFragmentFragment);

    const reviewsRef = useRef(null);
    const infoRef = useRef<HTMLDivElement>(null);

    const [accordionState, setAccordion] = useState([true, false, false, false, false]);
    const metadata = {
        title: appliance?.attributes?.meta_title?.values?.[0],
        description: appliance?.attributes?.meta_description?.values?.[0],
    };
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const allHeaters = useAllHeaters();

    const suggestionsCMS = data?.suggestionsCMS?.nodes?.[0];
    const isMobile = useDeviceDetect().isMobile();
    const isTablet = useDeviceDetect().isTablet();
    const responsiveImages = suggestionsCMS?.fields;
    const desktopImage = responsiveImages?.banniere_desktop?.[0]?.childImageSharp?.gatsbyImageData;
    const mobileImage = responsiveImages?.banniere_mobile?.[0]?.childImageSharp?.gatsbyImageData;
    const recommandedData = appliance?.attributes?.suggested_appliances?.values;
    let recommandedProducts: any[] = [];
    if (recommandedData) {
        const ids = recommandedData?.slice(0, 3).map((e) => e?.[currentLocale]);
        const arr = allHeaters.filter((prod: any) => ids.includes(prod?.code));
        recommandedProducts = arr as any[];
    }
    const APPLIANCE_PER_PAGE = isTablet ? 2 : isMobile ? 1 : 3; // By default the max recommended product will be 3 for desktop.
    const SCREEN_SLIDER_TRIGGER = 100;
    const [page, setPage] = useState(1);
    const [startSwipe, setStartSwipe] = useState(0);
    const [endSwipe, setEndSwipe] = useState(0);

    function openOptions() {
        setAccordion([false, true, false, false, false]);
    }

    function updateAccordionState(index: number) {
        setAccordion([
            index === 0 ? !accordionState[0] : accordionState[0],
            index === 1 ? !accordionState[1] : accordionState[1],
            index === 2 ? !accordionState[2] : accordionState[2],
            index === 3 ? !accordionState[3] : accordionState[3],
            index === 4 ? !accordionState[4] : accordionState[4],
        ]);
    }

    const setNext = () => {
        const pageNumber = Math.ceil(recommandedProducts.length / APPLIANCE_PER_PAGE);
        if (page !== pageNumber) setPage(page + 1);
        else setPage(1);
    };
    const setPrevious = () => {
        const pageNumber = Math.ceil(recommandedProducts.length / APPLIANCE_PER_PAGE);
        if (page !== 1) setPage(page - 1);
        else setPage(pageNumber);
    };
    function handleSwipe() {
        if (endSwipe - startSwipe > SCREEN_SLIDER_TRIGGER) {
            setPrevious();
        }
        if (endSwipe - startSwipe < -SCREEN_SLIDER_TRIGGER) {
            setNext();
        }
    }
    function Frame3D(props: { visao_id?: string; title: string; lang?: string }) {
        const { visao_id, title, lang = "fr" } = props;
        return (
            <div className="frame-container">
                <iframe
                    src={`https://demo.visao.ca/viewer/${visao_id}?lang=${lang}`}
                    title={title}
                    style={{ border: "none", width: "100%", height: "100%" }}
                    allow="autoplay; fullscreen; vr"
                    allowTransparency={true}
                    allowFullScreen={true}
                ></iframe>
            </div>
        );
    }

    useEffect(() => {
        handleSwipe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endSwipe]);

    useEffect(() => {
        // Si l'appareil n'est pas disponible pour le pays actuel (available_country),
        // on redirige vers l'archive de sa catégorie

        const isCurrentLocaleCA = currentLocale.includes("CA");
        const isCurrentLocaleUS = currentLocale.includes("US");

        if (
            (isCurrentLocaleCA || isCurrentLocaleUS) &&
            appliance?.attributes?.available_country?.values &&
            appliance.attributes.available_country.values.every(
                (value) =>
                    (isCurrentLocaleCA && value && value[currentLocale] === "United States") ||
                    (isCurrentLocaleUS && value && value[currentLocale] === "Canada")
            )
        ) {
            navigateTranslated(`${applianceType.link}/`);
        }
    });

    const { t } = useTranslation("layout");
    const applianceType = getApplianceType(appliance?.slug?.["en_CA"] as string, t);
    const visaoId = appliance?.attributes?.visao_id?.values?.[0]?.["en_CA"];
    const MemoizedFrame = useMemo(() => {
        const internalId = appliance?.attributes?.visao_id?.values?.[0]?.["en_CA"];
        if (!internalId) return undefined;
        const lang = currentLocale === "fr_CA" ? "fr" : "en";
        return (
            <Frame3D
                title={appliance?.code as string}
                visao_id={internalId}
                key={`frame-${appliance?.code}`}
                lang={lang}
            />
        );
    }, [appliance, currentLocale]);

    const currentPathArray = location.pathname.split("/").filter((path: string) => {
        // removes unwanted pathnames for breadcrumb navigation
        switch (path) {
            case "":
                return false;
            case "ca":
                return false;
            case "us":
                return false;
            case "fr":
                return false;
            case "en":
                return false;
            default:
                return true;
        }
    });
    const currentPath = currentPathArray.join("/");

    useEffect(() => {
        if (
            productService &&
            productPageTemplateInfo.live == undefined &&
            productPageTemplateInfo.static.code
        ) {
            productService.operations
                .getProduct(productPageTemplateInfo.static.code)
                .then((productLive) => {
                    if (productLive) {
                        setProductPageTemplateInfo({
                            live: productLive,
                            static: productPageTemplateInfo.static,
                        });
                    }
                });
        }
    }, [appliance]);

    return (
        <Layout>
            <SEO
                title={metadata?.title?.[currentLocale] as string}
                description={metadata?.description?.[currentLocale] as string}
                isDescriptionCurrentLocale={true}
            />
            <Breadcrumb
                elements={[
                    { name: t("appliance.home"), link: "/" },
                    {
                        name: applianceType.type,
                        link: `${applianceType.link}/`,
                    },
                    {
                        name: `${appliance?.title?.[currentLocale]}`,
                        link: `${currentPath}/`,
                    },
                ]}
            />
            <div className="container">
                <div className="appliance-page">
                    <div className="appliance-details">
                        <ApplianceImage
                            appliance={productPageTemplateInfo}
                            currentLocale={currentLocale}
                            visaoId={visaoId as string}
                            MemoizedFrame={MemoizedFrame}
                        />

                        <ApplianceDetails
                            appliance={productPageTemplateInfo}
                            reviews={reviews}
                            reviewsRef={reviewsRef}
                            infoRef={infoRef}
                            openOptions={openOptions}
                        />
                    </div>
                    <div className="appliance-info" ref={infoRef}>
                        {appliance && (
                            <ApplianceInfo
                                visaoId={visaoId as string}
                                appliance={appliance}
                                accordionState={accordionState}
                                updateAccordionState={updateAccordionState}
                            />
                        )}
                    </div>
                </div>
            </div>
            {recommandedProducts.length > 0 && (
                <div className="appliance-recommandations">
                    <GatsbyImage
                        image={(isMobile ? mobileImage : desktopImage) as IGatsbyImageData}
                        alt=""
                        className="recommandation-bg-img"
                    />
                    <div className="recommandation-text">
                        <div className="recommandation-title">
                            {suggestionsCMS?.attributes?.texte_1?.values?.[0]?.[currentLocale]}
                        </div>
                        <div className="recommandation-sugess">
                            {suggestionsCMS?.attributes?.texte_2?.values?.[0]?.[currentLocale]}
                        </div>
                        <div className="line"></div>
                    </div>
                    <div className="recommanded-appliance">
                        {recommandedProducts
                            ?.slice(APPLIANCE_PER_PAGE * (page - 1), APPLIANCE_PER_PAGE * page)
                            ?.map((appliance, index) => {
                                const appSingleReview = allProductReviews.find(
                                    (review) => review.sku === appliance?.code
                                );
                                return (
                                    <ApplianceSingle
                                        appliance={productPageTemplateInfo}
                                        reviews={appSingleReview}
                                        key={index}
                                        id={`appliance-recommendation-single-${index}`}
                                        onTouchStart={(e) => {
                                            setStartSwipe(e?.changedTouches[0].screenX);
                                        }}
                                        onTouchEnd={(e) => {
                                            setEndSwipe(e?.changedTouches[0].screenX);
                                        }}
                                    />
                                );
                            })}
                    </div>

                    <div className="buttons">
                        <img
                            className="left"
                            src={buttonArrow}
                            alt=""
                            onClick={() => setPrevious()}
                        ></img>
                        <img
                            className="right"
                            src={buttonArrow}
                            alt=""
                            onClick={() => setNext()}
                        ></img>
                    </div>
                </div>
            )}
            <div className="reviews-container">
                <ApplianceReviews
                    code={appliance?.code as string}
                    reviews={reviews}
                    ref={reviewsRef}
                />
            </div>
        </Layout>
    );
}

function getApplianceType(
    applianceType: string,
    t: any
): {
    type: string;
    link: string;
} {
    if (applianceType?.includes("wood-inserts")) {
        return {
            link: t("url:wood-inserts"),
            type: t("layout:meta-titles.wood-inserts"),
        };
    } else if (applianceType?.includes("wood-stoves")) {
        return {
            link: t("url:wood-stoves"),
            type: t("layout:meta-titles.wood-stoves"),
        };
    } else if (applianceType?.includes("pellet-stoves")) {
        return {
            link: t("url:pellet-stoves"),
            type: t("layout:meta-titles.appliance-pellet-stoves"),
        };
    } else if (applianceType?.includes("wood-furnaces")) {
        return {
            link: t("url:wood-furnaces"),
            type: t("layout:meta-titles.wood-furnaces"),
        };
    } else if (applianceType?.includes("cookstoves")) {
        return {
            link: t("url:cook-stoves"),
            type: t("layout:meta-titles.wood-cook-stoves"),
        };
    } else if (applianceType?.includes("outdoor")) {
        return {
            link: t("url:outdoor-fireplaces"),
            type: t("layout:meta-titles.outdoor-fireplaces"),
        };
    } else if (applianceType?.includes("camp-stove")) {
        return {
            link: t("url:camp-stove"),
            type: t("layout:meta-titles.camp-stove"),
        };
    }

    return {
        type: t(`appliance.appliance`),
        link: "/",
    };
}
